/* @jsxImportSource @emotion/react */

import React from 'react'
import { css } from '@emotion/react'
import classNames from 'classnames'
import { FormattedMessage, useIntl } from 'react-intl'
import { useData } from 'vike-react/useData'

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  SanityTextRenderer,
} from '@slite/components'
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  LinkedInIcon,
  LinkIcon,
  SliteLogoInCircleIcon,
  TwitterXIcon,
} from '@slite/icons'

import { Button } from '../../../components/Button/Button'
import { Section } from '../../../components/Section'
import { SectionCurve } from '../../../components/SectionCurve'
import { SectionLastCTA } from '../../../components/SectionLastCTA'
import { SlitePage } from '../../../components/SlitePage'
import { WebsiteText } from '../../../components/WebsiteText'
import { useRoutes } from '../../../routes'
import { MEDIUM_BREAKPOINT } from '../../../utils/breakpoints'
import { getTemplateCategories, getTemplateColorFromRemote } from '../constants'
import { SliteTemplateCard } from '../TemplateCard'
import type { Data } from './+data'

export default function TemplatePage() {
  const intl = useIntl()
  const routes = useRoutes()
  const { template, relatedTemplates } = useData<Data>()
  const color = getTemplateColorFromRemote(template.color)

  const link = `https://sliteuniverse.slite.com/api/redirect/app/importNote/${template.templateId}?from=website-template`

  const defaultLink = `https://slite.com/templates/${template.slug.current}`
  const twitterLink = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    `${template.name} by Slite, perfect for sharing with your team`
  )}%20https://slite.com/templates/${template.slug.current}`
  const linkedInLink = `https://www.linkedin.com/sharing/share-offsite/?url=https://slite.com/templates/${template.slug.current}`

  const templateCategories = getTemplateCategories(intl)
  const currentTemplateCategories = templateCategories.filter((category) =>
    template.templateCategories?.includes(category.value)
  )

  const description = (
    <>
      <p className="font-weight-semibold font-size-5 margin-bottom-32">
        {template.articleDescription}
      </p>
      <Button
        href={link}
        target="_blank"
        variant="primary"
        className="margin-bottom-32"
        rel="noreferrer"
      >
        <FormattedMessage
          id="template.page.startWithThisDoc"
          defaultMessage="Start with this doc"
        />
      </Button>

      <div className="margin-bottom-40">
        <WebsiteText
          variant="smallUppercase"
          className="font-size-8 margin-bottom-12"
          style={{
            color: 'rgba(0,0,0,0.4)',
          }}
        >
          <FormattedMessage
            id="template.page.share"
            defaultMessage="Share this template"
          />
        </WebsiteText>

        <div className="flexbox flexbox-column-gap-20">
          <a
            href={twitterLink}
            className="shade-05 shade-10-on-hover"
            target="_blank"
            rel="noreferrer"
          >
            <TwitterXIcon size={20} />
          </a>
          <a
            href={linkedInLink}
            className="shade-05 shade-10-on-hover"
            target="_blank"
            rel="noreferrer"
          >
            <LinkedInIcon size={20} />
          </a>
          <button
            onClick={() => navigator.clipboard.writeText(defaultLink)}
            className="shade-05 shade-10-on-hover pointer border-none background-transparent"
          >
            <LinkIcon size={20} />
          </button>
        </div>
      </div>
    </>
  )

  const getIndexCategoryLink = (category: string) => {
    const basePath = routes.templates.index()
    if (category === 'all') {
      return basePath
    }
    return `${basePath}?category=${category}`
  }

  return (
    <SlitePage
      canonicalUrl={routes.templates.detail({ slug: template.slug.current })}
      title={template.openGraphTitle || template.name}
      description={template.metaDescription ?? template.articleDescription}
      background={color}
      ogImage={template.openGraphImage?.url}
      footerBackground="moon-dust"
    >
      <Section background={color} maxWidth={1440}>
        <a
          href={routes.templates.index()}
          className="font-weight-semibold block margin-bottom-32"
          style={{
            color: 'rgba(0,0,0,0.4)',
          }}
        >
          <FormattedMessage
            defaultMessage="{icon} More templates"
            id="template.page.backToTemplates"
            values={{ icon: <ArrowLeftIcon style={{ marginTop: -4 }} /> }}
          />
        </a>
        <WebsiteText variant="display" className={'margin-bottom-20'}>
          {template.name}
        </WebsiteText>
        <div className="margin-bottom-40 margin-bottom-20-medium margin-bottom-20-small flexbox flexbox-column-gap-8 flexbox-align-center">
          {template.author ? (
            <span className="font-family-title font-weight-medium font-size-7">
              {template.author.name}
            </span>
          ) : (
            <>
              <SliteLogoInCircleIcon size={28} />
              <span className="font-family-title font-weight-medium font-size-7">
                <FormattedMessage
                  defaultMessage="By Slite"
                  id="template.page.bySlite"
                />
              </span>
            </>
          )}
        </div>

        <div className="grid-12 grid-gap-40 grid-gap-0-small">
          <div className="grid-span-9 grid-span-12-medium grid-span-12-small">
            <iframe
              title={template.name}
              scrolling="yes"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation"
              src={`https://sliteuniverse.slite.com/api/s/${template.templateId}?copy=none`}
              className="border-none radius-semilarger width-full"
              css={css`
                aspect-ratio: 4 / 3;

                @media (max-width: ${MEDIUM_BREAKPOINT}px) {
                  aspect-ratio: 1 / 1;
                }
              `}
            />
          </div>
          <div className="grid-span-3 grid-span-12-medium grid-span-12-small grid-justify-start">
            {description}
          </div>
        </div>
      </Section>
      <SectionCurve
        from={color}
        to="shade-100"
        shape="top-large"
        style={{ marginTop: 0, zIndex: 0 }}
      />
      <Section containerTopPadding={0} maxWidth={1048}>
        <div className="simple-rich-text simple-rich-text-sanity large">
          <SanityTextRenderer
            variant="website"
            content={template.articleContent}
          />
        </div>

        {template.templateCategories?.length && (
          <div className="margin-top-40 flexbox flexbox-column-gap-8">
            {currentTemplateCategories.map((category) => {
              const link = getIndexCategoryLink(category.value)

              return (
                <a
                  href={link}
                  key={category.value}
                  className="font-family-title font-weight-medium border-small border-shade-05 padding-horizontal-16 padding-vertical-8 radius-largest pointer background-shade-100 shade-00 background-shade-00-on-hover shade-100-on-hover transition"
                >
                  {category.title}
                </a>
              )
            })}
          </div>
        )}
      </Section>
      {template.faq.length > 0 && (
        <Section maxWidth={1048}>
          <WebsiteText variant="title" className="margin-bottom-12">
            <FormattedMessage
              id="template.page.faqsTitle"
              defaultMessage="Questions & answers"
            />
          </WebsiteText>
          <WebsiteText variant="body" className="shade-20 margin-bottom-24">
            <FormattedMessage
              id="template.page.faqsDescription"
              defaultMessage="Can't find what you're looking for? Visit our {helpCenterLink}."
              values={{
                helpCenterLink: (
                  <a
                    href={routes.external.helpCenter.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shade-20 shade-30-on-hover underline"
                  >
                    <FormattedMessage
                      defaultMessage="Help Center"
                      id="template.page.faqsDescription.helpCenterLink"
                    />
                  </a>
                ),
              }}
            />
          </WebsiteText>
          <div>
            {template.faq.map((item) => (
              <div
                className="border-bottom-small border-shade-40"
                key={item.question}
              >
                <Collapsible>
                  <CollapsibleTrigger>
                    {({ isExpanded, onToggleExpand }) => (
                      <button
                        className="font-family-title font-size-4 background-transparent border-none padding-vertical-20 width-full text-align-left pointer font-weight-medium flexbox flexbox-space-between flexbox-align-center shade-05"
                        onClick={onToggleExpand}
                      >
                        <span className="flexbox-grow">{item.question}</span>

                        <ChevronDownIcon
                          size={14}
                          className={classNames(
                            'shade-30 flexbox-no-shrink',
                            isExpanded ? 'rotate-180' : ''
                          )}
                        />
                      </button>
                    )}
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    {() => (
                      <WebsiteText
                        variant="body"
                        className="font-weight-semibold padding-bottom-32 shade-05"
                      >
                        {item.answer}
                      </WebsiteText>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              </div>
            ))}
          </div>
        </Section>
      )}

      <SectionCurve
        from="moon-dust"
        to="shade-100"
        shape="top-large"
        style={{ marginTop: 0, zIndex: 0, transform: 'rotate(180deg)' }}
      />

      <Section background="moon-dust" maxWidth={1048}>
        <WebsiteText
          variant="smallTitle"
          className="font-family-title margin-bottom-32"
          css={css`
            font-weight: var(--font-weight-medium);
          `}
        >
          <FormattedMessage
            defaultMessage="Related templates"
            id="template.page.relatedTemplatesTitle"
          />
        </WebsiteText>

        <div className="grid-12 grid-gap-32">
          {relatedTemplates.map((relatedTemplate) => (
            <SliteTemplateCard
              slug={relatedTemplate.slug.current}
              title={relatedTemplate.name}
              description={relatedTemplate.articleDescription}
              key={relatedTemplate._id}
              imageUrl={relatedTemplate.image?.url || ''}
              imageAlt={relatedTemplate.name}
              color={getTemplateColorFromRemote(relatedTemplate.color)}
            />
          ))}
        </div>
      </Section>
      <SectionLastCTA background="moon-dust" />
    </SlitePage>
  )
}
